/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, LinearProgress, IconButton, Stack, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Typography, Button, Chip, MenuItem, Menu, useMediaQuery, useTheme, Autocomplete, Grid, FormControl, Tab, Tabs, Select } from '@mui/material';
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { DataGridPro, DataGridProProps, useGridApiRef } from "@mui/x-data-grid-pro";
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { GridColDef, GridLinkOperator, GridToolbarFilterButton, GridToolbarContainer, 
    getGridStringOperators, GridToolbarColumnsButton, GridToolbarDensitySelector, GridToolbarExport
} from '@mui/x-data-grid';
import { GridFilterModel, GridSortModel } from '@mui/x-data-grid-pro';
import { RiExpandUpDownLine } from "react-icons/ri";
import { apiClient } from '../helper/api';
import { FaDownload, FaPaperPlane, FaPlus, FaRegEye, FaTrash } from 'react-icons/fa';
import CancelIcon from "@mui/icons-material/Cancel";
import { getRowsPerPageOptions } from '../components/AddEdit/CustomFunctions';
import Swal from 'sweetalert2';
import useAuth from '../context/AuthProvider';
import { AddReportTag } from '../components/AddEdit/AddReportTag';
import JSZip from 'jszip';
import { formatUtcToLocal } from '../helper/utils';
import { LightBlueBtnColor, GreenBtnColor, OrangeBtnColor, PrimaryBlueColor } from '../helper/theme';
import { DocumentViewer } from '../components/Elements/DocumentViewer';
import { KeyboardArrowUp, KeyboardArrowDown } from '@mui/icons-material';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SettingsBackupRestoreOutlinedIcon from '@mui/icons-material/SettingsBackupRestoreOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import { Search, Clear, Refresh } from '@mui/icons-material';
import { useExtractionContext } from '../context/ExtractionContext';
import { MiscReportTagMessageDialog } from '../components/Elements/MiscNotificationDialogs';
export interface ReportTagModel {
    reportTagId?: number;
    tagNumber: string;
    state: string;
    stateId?: number;
    isMultipleTags?: boolean;
    insuredName?: string;
    invoiceNumber?: string;
    policyNumber?: string;
    partnerId: number;
    agency: string;  // agency/client/partner name
    sentOn: string;
    sentBy: string;
    filerNotes?: string;
    responseReceivedOn: string;
    clientInHouseCloseRequestedAt?: string | null;
    dueDate?: string;
    isDocsUploaded?: boolean;  // for checking if we will display the documents attached and download buttons
    isClosed?: boolean;
    errorTagCodeIds: number[];
    rowNum?: number;
    assignedToUserId?: number | null;
    assignedToUserName?: string | null;
    workflowStatus?: string | null;
};
export interface ReportTagResponseModel {
    reportTags: ReportTagModel[];
    totalCount: number;
};
export interface ErrorTagCodeModel {
    errorTagCodeId: number;
    errorTagName: string;
    messageTemplate?: string | null;
};

export interface ReportTagDocument {
    reportTagId: number;
    reportTagDocumentId: number;
    documentUrl: string;
    documentName: string;
    createdByUserId: number;
    createdByUsername: string;
    createdByUserRole?: string;
    dateCreated: Date;
};

export enum TagStatus {
    All = 'All',
    Resolved = 'Resolved',  // response received
    Unresolved = 'Unresolved',  // no response received
};

/** Open vs Closed list. Closed is IsClosed, separate from the client inbox archive flag. */
export enum ReportTagTabNames {
    Open = 'OPEN',
    Archive = 'ARCHIVE',
}

/** Grid shows Username `miscstaff` as "MISC team" — map that label so Sent By filters hit the stored username. */
function mapReportTagSentByFilterValue(raw: string): string {
    const t = raw.trim();
    if (!t) return raw;
    if (/^misc\s*[-]?\s*team$/i.test(t)) return 'miscstaff';
    return raw;
}

function applyReportTagSentByAliasToFilterModel(model: GridFilterModel): GridFilterModel {
    return {
        ...model,
        items: model.items.map((item) => {
            if (item.columnField !== 'sentBy' || item.value == null) return item;
            const str = String(item.value);
            const mapped = mapReportTagSentByFilterValue(str);
            return mapped !== str ? { ...item, value: mapped } : item;
        }),
    };
}

const DownloadFileBtn = () => (
    <svg width="20" height="20" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M19.25 13.75V17.4167C19.25 17.9029 19.0568 18.3692 18.713 18.713C18.3692 19.0568 17.9029 19.25 17.4167 19.25H4.58333C4.0971 19.25 3.63079 19.0568 3.28697 18.713C2.94315 18.3692 2.75 17.9029 2.75 17.4167V13.75M6.41667 9.16667L11 13.75M11 13.75L15.5833 9.16667M11 13.75V2.75" stroke="#02A4E3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const EditPolicyBtn = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M17 3C17.2626 2.73735 17.5744 2.52901 17.9176 2.38687C18.2608 2.24473 18.6286 2.17157 19 2.17157C19.3714 2.17157 19.7392 2.24473 20.0824 2.38687C20.4256 2.52901 20.7374 2.73735 21 3C21.2626 3.26264 21.471 3.57444 21.6131 3.9176C21.7553 4.26077 21.8284 4.62856 21.8284 5C21.8284 5.37143 21.7553 5.73923 21.6131 6.08239C21.471 6.42555 21.2626 6.73735 21 7L7.5 20.5L2 22L3.5 16.5L17 3Z" stroke="#02A4E3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

/** One-line preview in the grid row; full formatted text opens in a dialog (row height stays default). */
const FilerNotesGridCell: React.FC<{ text: string; onViewFull: () => void }> = ({ text, onViewFull }) => {
    const normalized = text.replace(/\r\n/g, '\n').trim();
    if (!normalized) return null;
    const previewPlain = normalized.replace(/\n+/g, ' ').replace(/\s{2,}/g, ' ').trim();
    const likelyTruncated =
        normalized.length > 90 || (normalized.match(/\n/g)?.length ?? 0) >= 1;

    return (
        <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                width: '100%',
                minWidth: 0,
            }}
        >
            <Typography
                component="span"
                variant="body2"
                sx={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: '13px',
                    lineHeight: 1.4,
                    color: '#334155',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
                title={previewPlain}
            >
                {previewPlain}
            </Typography>
            {likelyTruncated ? (
                <Button
                    variant="text"
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        onViewFull();
                    }}
                    sx={{
                        flexShrink: 0,
                        p: '2px 6px',
                        minWidth: 0,
                        fontSize: '12px',
                        textTransform: 'none',
                        fontWeight: 600,
                        color: PrimaryBlueColor,
                        '&:hover': { backgroundColor: 'rgba(32, 153, 210, 0.06)' },
                    }}
                >
                    View
                </Button>
            ) : null}
        </Box>
    );
};

/** Full filer note text with paragraph breaks and **bold** segments (e.g. in dialog). */
const FilerNotesCellContent: React.FC<{ text: string }> = ({ text }) => {
    const normalized = text.replace(/\r\n/g, '\n').trim();
    if (!normalized) return null;
    const blocks = normalized.split(/\n{2,}/).map((b) => b.trim()).filter(Boolean);
    const toRender = blocks.length > 0 ? blocks : [normalized];

    const renderInline = (block: string) => {
        const parts = block.split(/(\*\*[^*]+\*\*)/g);
        return parts.map((part, i) => {
            if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
                return <strong key={i}>{part.slice(2, -2)}</strong>;
            }
            return <React.Fragment key={i}>{part}</React.Fragment>;
        });
    };

    return (
        <Stack spacing={1.15} sx={{ width: '100%', py: 0.25, alignSelf: 'stretch' }}>
            {toRender.map((block, idx) => (
                <Typography
                    key={idx}
                    component="div"
                    variant="body2"
                    sx={{
                        m: 0,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        lineHeight: 1.55,
                        fontSize: '13px',
                        color: '#334155',
                    }}
                >
                    {renderInline(block)}
                </Typography>
            ))}
        </Stack>
    );
};


export const ReportTag: React.FC = () => {
    const { user } = useAuth();
    const token = user?.token;
    const [loading, setLoading] = React.useState(false);
    const [currentPage, setCurrentPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [reportTags, setReportTags] = useState<ReportTagModel[]>([]);
    const [selectedRows, setSelectedRows] = useState<ReportTagModel[]>([]);
    const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
    const [sortModel, setSortModel] = useState<GridSortModel>([]);
    const theme = useTheme();
    const isTabletOrDown = useMediaQuery(theme.breakpoints.down('md'));
    const reportTagGridApiRef = useGridApiRef();
    const [addReportTagOpen, setAddReportTagOpen] = React.useState(false);
    const [openDialogAfterLoad, setOpenDialogAfterLoad] = React.useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const {
        extractionInProgress,
        extractionComplete,
        extractedValues,
        setExtractionComplete,
        clearExtraction,
        setIsOnReportTagPage,
        onExtractionSuccess,
    } = useExtractionContext();

    // Open the AddReportTag dialog only after extractedValues is confirmed in React state,
    // so the Formik lazy initializer sees the populated values and pre-fills the form.
    useEffect(() => {
        if(openDialogAfterLoad && extractedValues.length > 0){
            setAddReportTagOpen(true);
            setOpenDialogAfterLoad(false);
        }
    }, [openDialogAfterLoad, extractedValues]);

    useEffect(() => {
        setIsOnReportTagPage(true);
        return () => setIsOnReportTagPage(false);
    }, [setIsOnReportTagPage]);

    // When the page is opened via the email "Review & Submit Tags" link, load the
    // pending extraction from blob storage and pre-populate the AddReportTag dialog.
    useEffect(() => {
        const extractionId = searchParams.get('extractionId');
        if(!extractionId) return;

        // Strip the param from the URL so a page refresh doesn't re-fetch a consumed blob
        setSearchParams((prev) => {
            prev.delete('extractionId');
            return prev;
        }, { replace: true });

        apiClient(`/ReportTags/getPendingTagExtraction?extractionId=${extractionId}`, {
            method: 'GET',
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            }),
        }).then(async (responseJson) => {
            if(!responseJson?.status || !responseJson?.data){
                Swal.fire({
                    title: 'Link expired',
                    text: 'These extraction results have already been used or the link has expired.',
                    icon: 'warning',
                });
                return;
            }
            const { records, partnersId, stateId, fileName, pdfUrl } = responseJson.data;

            // Fetch the original PDF so it stays attached when the tags are sent to the client.
            let pdfFiles: File[] = [];
            if(pdfUrl && fileName){
                try{
                    const pdfResponse = await fetch(pdfUrl);
                    const pdfBlob = await pdfResponse.blob();
                    pdfFiles = [new File([pdfBlob], `${fileName}.pdf`, { type: 'application/pdf' })];
                }catch(pdfErr){
                    console.error('Failed to fetch extraction PDF:', pdfErr);
                }
            }

            const mappedValues = (records ?? []).map((record: any) => {
                const raw = record?.dueDate ?? '';
                const s = String(raw).trim();
                let dueDateStr = '';
                if(s){
                    const isoCandidate = s.split('T')[0].slice(0, 10);
                    if(/^\d{4}-\d{2}-\d{2}$/.test(isoCandidate)){
                        dueDateStr = isoCandidate;
                    } else {
                        const us = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/.exec(s);
                        if(us) dueDateStr = `${us[3]}-${us[1].padStart(2,'0')}-${us[2].padStart(2,'0')}`;
                    }
                }
                return {
                    partnersId: partnersId ?? 0,
                    stateId: stateId ?? 0,
                    numberOfTags: 'single',
                    invoiceNumber: record?.invoiceNumber ?? '',
                    insuredName: record?.insuredName ?? '',
                    policyNumber: record?.policyNumber ?? '',
                    tagNumber: Number(record?.tagNumber),
                    errorTagCodes: record?.errorTagCodeIds ?? [],
                    filerNotes: record?.filerNotes ?? '',
                    dueDate: dueDateStr,
                    files: pdfFiles,
                };
            });
            onExtractionSuccess(mappedValues);
            setOpenDialogAfterLoad(true);
        }).catch((err) => {
            console.error('Failed to load pending extraction:', err);
        });
    }, [onExtractionSuccess, searchParams, setSearchParams, token]);
    const [filerNotesFullDialog, setFilerNotesFullDialog] = React.useState<{
        open: boolean;
        text: string;
        title: string;
    }>({ open: false, text: '', title: '' });

    const [states, setStates] = useState<{ stateId: number, stateName: string, postalAbbv: string }[]>([{ stateId: 0, stateName: "", postalAbbv: "" }]);
    const [partners, setPartners] = useState<{ partnersId: number, customerName: string }[]>([{ partnersId: 0, customerName: "" }]);
    // use this default data until error tag codes are in the db
    const [errorTagCodes, setErrorTagCodes] = useState<ErrorTagCodeModel[]>([]);

    const [filterBarPartner, setFilterBarPartner] = useState<{ partnersId: number; customerName: string } | null>(null);
    const [filterBarState, setFilterBarState] = useState<{ stateId: number; stateName: string; postalAbbv: string } | null>(null);
    const [filterBarSentBy, setFilterBarSentBy] = useState('');
    const [reportTagListTab, setReportTagListTab] = useState<ReportTagTabNames>(ReportTagTabNames.Open);

    // Document viewer state
    const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
    const [documentViewerUrl, setDocumentViewerUrl] = useState<string | null>(null);
    const [documentViewerFileName, setDocumentViewerFileName] = useState<string | null>(null);
    const [viewingDocument, setViewingDocument] = useState(false);
    
    // Document list state (for multiple documents)
    const [documentListOpen, setDocumentListOpen] = useState(false);
    const [documentList, setDocumentList] = useState<ReportTagDocument[]>([]);
    const [currentDocumentIndex, setCurrentDocumentIndex] = useState<number>(-1);

    const [miscMessageCenterOpen, setMiscMessageCenterOpen] = useState(false);
    const [miscMessageCenterReportTagId, setMiscMessageCenterReportTagId] = useState(0);

    const openMiscMessageCenter = (id: number) => {
        if (!id) return;
        setMiscMessageCenterReportTagId(id);
        setMiscMessageCenterOpen(true);
    };

    const handleReportTagTabChange = (tab: ReportTagTabNames) => {
        if (tab !== reportTagListTab) {
            setReportTagListTab(tab);
            setCurrentPage(1);
        }
    };

    const handleCloseAddReportTag = () => {
        setAddReportTagOpen(false);
        // Only clear extraction state when nothing is running — if extraction is in
        // progress the context needs to stay alive so the result can land after navigation.
        if (!extractionInProgress) {
            clearExtraction();
        }
        // reload the report data
        getAllReportTags();
        setSelectedRows([]);
    };

    const [markAsUnresolvedOpen, setMarkAsUnresolvedOpen] = useState(false);
    const [markAsUnresolvedReportTagId, setMarkAsUnresolvedReportTagId] = useState<number | null>(null);

    const openMarkAsUnresolvedDialog = (reportTagId: number) => {
        setMarkAsUnresolvedReportTagId(reportTagId);
        setMarkAsUnresolvedOpen(true);
    };

    const closeMarkAsUnresolvedDialog = () => {
        setMarkAsUnresolvedOpen(false);
        setMarkAsUnresolvedReportTagId(null);
    };

    const sendTagBackToClient = () => {
        if (markAsUnresolvedReportTagId == null) return;
        setLoading(true);
        const reportTagId = markAsUnresolvedReportTagId;
        apiClient(`/ReportTags/markTagAsUnresolved?reportTagId=${reportTagId}`, {
            method: 'POST',
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            }),
        }).then((response) => {
            if (!response) return;
            Swal.fire({
                title: 'Tag Marked as Unresolved',
                text: 'The tag has been marked as unresolved and sent back to the client',
                icon: 'success',
                confirmButtonText: 'OK',
            });
            closeMarkAsUnresolvedDialog();
            getAllReportTags();
            setSelectedRows([]);
        }).catch((error) => {
            console.error("Error marking tag as unresolved:", error);
            Swal.fire({
                title: 'Error',
                text: 'Failed to mark tag as unresolved',
                icon: 'error',
                confirmButtonText: 'OK',
            });
        }).finally(() => {
            setLoading(false);
        });
    };

    const downloadSelectedReportTagDocuments = async (allDocuments: boolean = false, rowsToDownloadOverride?: ReportTagModel[]) => {
        // Determine which rows to download
        let rowsToDownload: ReportTagModel[];
        
        if (rowsToDownloadOverride && rowsToDownloadOverride.length > 0) {
            // Use the provided rows (e.g., from action column click)
            setSelectedRows(rowsToDownloadOverride);
            rowsToDownload = rowsToDownloadOverride;
        } else if (allDocuments) {
            // Use all documents
            rowsToDownload = reportTags;
            
            // Check if there are any documents available
            if (rowsToDownload.length === 0) {
                Swal.fire({
                    icon: 'error',
                    title: 'No Documents Available',
                    text: 'There are no documents available to download.',
                });
                return;
            }
        } else {
            // When downloading selected documents, check if any are selected
            // Check both that selectedRows exists and has items
            const hasSelectedRows = selectedRows && Array.isArray(selectedRows) && selectedRows.length > 0;
            if (!hasSelectedRows) {
                Swal.fire({
                    icon: 'error',
                    title: 'No Documents Selected',
                    text: 'Please select at least one document to download.',
                });
                return;
            }
            rowsToDownload = selectedRows;
        }
        
        // create a folder with the selected documents
        const folderName = `${rowsToDownload[0].agency} - Report Tag #${rowsToDownload[0].tagNumber} - Documents`;
        
        const confirmResult = await Swal.fire({
            title: '<div style="color: white;">Download Documents</div>',
            html: `<div style="color: white;">This will download all${allDocuments ? '' : ' selected'} documents to a zip file named <strong>${folderName}.zip</strong></div>`,
            showCancelButton: true,
            confirmButtonColor: GreenBtnColor,
            cancelButtonColor: OrangeBtnColor,
            reverseButtons: true,
            background: PrimaryBlueColor,
        });
        
        if (!confirmResult.isConfirmed) {
            setSelectedRows([]);
            return;
        }
        
        setLoading(true);
        
        // Show loading dialog
        Swal.fire({
            title: 'Preparing Download...',
            html: 'Please wait while we prepare your files for download.',
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });
        
        try {
            const zip = new JSZip();
            let successCount = 0;
            let failCount = 0;
            const errors: string[] = [];
            const fetchErrors: string[] = [];

            // Fetch documents for all selected rows
            const allDocuments: ReportTagDocument[] = [];
            
            // Fetch documents for each report tag
            await Promise.all(
                rowsToDownload.map(async (row) => {
                    if (!row.reportTagId) return;
                    
                    try {
                        const response = await apiClient(`/ReportTags/getReportTagDocumentsById/${row.reportTagId}`, {
                            method: 'GET',
                            headers: new Headers({
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`,
                            })
                        });
                        
                        if (response && response.data && response.data.documents && response.data.documents.length > 0) {
                            const documents: ReportTagDocument[] = response.data.documents.map((doc: any) => ({
                                reportTagId: doc.reportTagId || 0,
                                reportTagDocumentId: doc.reportTagDocumentId || 0,
                                documentUrl: doc.documentUrl,
                                documentName: doc.documentName || 'Document',
                                createdByUserId: doc.createdByUserId || 0,
                                createdByUsername: doc.createdByUsername || 'Unknown',
                                createdByUserRole: doc.createdByUserRole || '',
                                dateCreated: new Date(doc.dateCreated) || '',
                            }));
                            allDocuments.push(...documents);
                        }
                    } catch (error) {
                        console.error(`Error fetching documents for reportTagId ${row.reportTagId}:`, error);
                        fetchErrors.push(`Tag #${row.tagNumber}`);
                    }
                })
            );

            if (allDocuments.length === 0) {
                Swal.close();
                const errorMessage = fetchErrors.length > 0 
                    ? `No documents were found. Failed to fetch documents for: ${fetchErrors.join(', ')}`
                    : 'No documents were found for the selected tags.';
                Swal.fire({
                    icon: 'error',
                    title: 'No Documents Found',
                    text: errorMessage,
                    confirmButtonText: 'OK',
                });
                return;
            }
            
            const usedFileNames = new Set<string>();
            // Helper function to generate unique filename (Chrome-style numbering)
            const generateUniqueFileName = (baseFileName: string): string => {
                const fileExtension = baseFileName.includes('.') 
                    ? baseFileName.substring(baseFileName.lastIndexOf('.'))
                    : '';
                const fileNameWithoutExt = baseFileName.includes('.')
                    ? baseFileName.substring(0, baseFileName.lastIndexOf('.'))
                    : baseFileName;
                
                let uniqueFileName = baseFileName;
                let counter = 0;
                
                // Keep trying until we find a unique name
                while (usedFileNames.has(uniqueFileName)) {
                    counter++;
                    uniqueFileName = `${fileNameWithoutExt} (${counter})${fileExtension}`;
                }
                
                usedFileNames.add(uniqueFileName);
                return uniqueFileName;
            };
            
            // Generate unique filenames for all files first
            const filesWithUniqueNames = allDocuments.map((item: ReportTagDocument) => ({
                item,
                uniqueFileName: generateUniqueFileName(item.documentName || `document_${item.reportTagDocumentId}`)
            }));
            
            // Fetch each file and add to zip
            await Promise.all(
                filesWithUniqueNames.map(async ({ item, uniqueFileName }) => {
                    try {
                        // Fetch the file from documentUrl
                        const response = await fetch(item.documentUrl, {
                            method: 'GET',
                            mode: 'cors',
                            credentials: 'omit',
                            cache: 'no-cache',
                        });
                        
                        if (!response.ok) {
                            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                        }
                        
                        // Get the blob (preserves original content type)
                        const blob = await response.blob();
                        
                        // Add file to zip
                        zip.file(uniqueFileName, blob);
                        successCount++;
                    } catch (error: any) {
                        failCount++;
                        const errorMsg = error.message || 'Failed to download';
                        errors.push(`${item.documentName}: ${errorMsg}`);
                        console.error(`Error downloading ${item.documentName} from ${item.documentUrl}:`, error);
                        console.error('Error details:', error);
                    }
                })
            );

            if (successCount === 0) {
                throw new Error('Failed to download any documents');
            }

            // Generate zip file
            const zipBlob = await zip.generateAsync({ type: 'blob' });
            
            // Create download link
            const url = window.URL.createObjectURL(zipBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${folderName}.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            Swal.close();
            
            // Show success/partial success message
            const hasFetchErrors = fetchErrors.length > 0;
            const hasDownloadErrors = failCount > 0;
            
            if (hasFetchErrors || hasDownloadErrors) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Partial Success',
                    html: `
                        <p><strong>${successCount}</strong> document(s) downloaded successfully.</p>
                        ${hasDownloadErrors ? `<p><strong>${failCount}</strong> document(s) failed to download.</p>` : ''}
                        ${hasFetchErrors ? `<p><strong>${fetchErrors.length}</strong> tag(s) failed to fetch documents: ${fetchErrors.join(', ')}</p>` : ''}
                        ${errors.length > 0 ? `<p style="font-size: 12px; color: #666; margin-top: 10px;">Download Errors:<br>${errors.slice(0, 3).join('<br>')}${errors.length > 3 ? '<br>...' : ''}</p>` : ''}
                    `,
                    confirmButtonText: 'OK',
                });
            } else {
                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: `All ${successCount} document(s) have been downloaded successfully.`,
                    confirmButtonText: 'OK',
                });
            }
        } catch (error: any) {
            console.error('Error in downloadSelectedReportTagDocuments:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'An error occurred while downloading the documents.',
                confirmButtonText: 'OK',
            });
        } finally {
            setLoading(false);
            setSelectedRows([]);
        }
    };

    const filterOperators = getGridStringOperators().filter(({ value }) => ['contains'].includes(value));
    const columns: GridColDef[] = [
        { field: 'Action', headerName: 'Action', flex: 0.2, minWidth: 72, maxWidth: 88,
            filterable: false, 
            sortable: false,
            disableColumnMenu: true,
            disableExport: true,
            hideSortIcons: true,
            headerAlign: 'left', align: 'left',
            renderCell: (params) => {
                const reportTagId = params.row.reportTagId;
                const canOpen = reportTagId != null && reportTagId > 0;

                return (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        gap: 0,
                        width: '100%',
                    }}
                >
                    {canOpen ? (
                        <Tooltip title="Open" arrow>
                            <IconButton
                                size="small"
                                aria-label="Open"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    openMiscMessageCenter(reportTagId);
                                }}
                            >
                                <MailOutlineIcon fontSize="small" color="action" />
                            </IconButton>
                        </Tooltip>
                    ) : null}
                </Box>
            )}
        },
        { field: 'agency', headerName: 'Client',
            hideSortIcons: true,
            renderHeader: (params) => {
                const sortItem = sortModel.find(
                    (item: any) => item.field === 'agency'
                  );
            
                const isSortedAsc = sortItem?.sort === 'asc';
                const isSortedDesc = sortItem?.sort === 'desc'
                return (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
                </div>
            )
            }, type: 'string',
            flex: 0.5, minWidth: 200, align: 'left', headerAlign: 'left', filterOperators
        },
        { field: 'state', headerName: 'State',
            hideSortIcons: true,
            renderHeader: (params) => {
                const sortItem = sortModel.find(
                    (item: any) => item.field === 'state'
                  );
            
                const isSortedAsc = sortItem?.sort === 'asc';
                const isSortedDesc = sortItem?.sort === 'desc'
                return (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
                </div>
            )}, type: 'string',
            flex: 0.5, minWidth: 100, align: 'left', headerAlign: 'left', filterOperators
        },
        { field: 'invoiceNumber', headerName: 'Invoice Number',
            hideSortIcons: true,
            renderHeader: (params) => {
                const sortItem = sortModel.find(
                    (item: any) => item.field === 'invoiceNumber'
                  );
            
                const isSortedAsc = sortItem?.sort === 'asc';
                const isSortedDesc = sortItem?.sort === 'desc'
                return (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
                </div>
            )}, type: 'string',
            flex: 0.5, minWidth: 175, align: 'left', headerAlign: 'left', filterOperators,
            renderCell: (params) => (
                <div>{params.row.isMultipleTags ? '' : (params.value ?? '')}</div>
            ),
        },
        { field: 'policyNumber', headerName: 'Policy Number',
            hideSortIcons: true,
            renderHeader: (params) => {
                const sortItem = sortModel.find(
                    (item: any) => item.field === 'policyNumber'
                  );
            
                const isSortedAsc = sortItem?.sort === 'asc';
                const isSortedDesc = sortItem?.sort === 'desc'
                return (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
                </div>
            )}, type: 'string',
            flex: 0.5, minWidth: 170, align: 'left', headerAlign: 'left', filterOperators,
            renderCell: (params) => (
                <div>{params.row.isMultipleTags ? '' : (params.value ?? '')}</div>
            ),
        },
        { field: 'insuredName', headerName: 'Insured Name',
            hideSortIcons: true,
            renderHeader: (params) => {
                const sortItem = sortModel.find(
                    (item: any) => item.field === 'insuredName'
                  );
            
                const isSortedAsc = sortItem?.sort === 'asc';
                const isSortedDesc = sortItem?.sort === 'desc'
                return (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
                </div>
            )}, type: 'string',
            flex: 0.5, minWidth: 170, align: 'left', headerAlign: 'left', filterOperators,
            renderCell: (params) => (
                <div>{params.row.isMultipleTags ? '' : (params.value ?? '')}</div>
            ),
        },
        { field: 'tagNumber', headerName: 'Tag Number',
            hideSortIcons: true,
            renderHeader: (params) => {
                const sortItem = sortModel.find(
                    (item: any) => item.field === 'tagNumber'
                  );
            
                const isSortedAsc = sortItem?.sort === 'asc';
                const isSortedDesc = sortItem?.sort === 'desc'
                return (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
                </div>
            )}, type: 'string',
            flex: 0.5, minWidth: 150, align: 'left', headerAlign: 'left', filterOperators,
            valueGetter: (params) => (params.value != null ? String(params.value) : ''),
        },
        { field: 'filerNotes', headerName: 'Filer Notes',
            hideSortIcons: true,
            renderHeader: (params) => {
                const sortItem = sortModel.find(
                    (item: any) => item.field === 'filerNotes'
                  );
            
                const isSortedAsc = sortItem?.sort === 'asc';
                const isSortedDesc = sortItem?.sort === 'desc'
                return (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
                </div>
            )}, type: 'string',
            flex: 1, minWidth: 280, align: 'left', headerAlign: 'left', filterOperators,
            renderCell: (params) => {
                const raw = params.value != null ? String(params.value) : '';
                if (!raw.trim()) return <Box sx={{ width: '100%' }} />;
                const title = params.row.tagNumber
                    ? `Filer notes – Tag ${params.row.tagNumber}`
                    : 'Filer notes';
                return (
                    <FilerNotesGridCell
                        text={raw}
                        onViewFull={() =>
                            setFilerNotesFullDialog({ open: true, text: raw, title })
                        }
                    />
                );
            },
        },
        { field: 'sentOn', headerName: 'Sent On', 
            hideSortIcons: true,
            renderHeader: (params) => {
                const sortItem = sortModel.find(
                    (item: any) => item.field === 'sentOn'
                  );
            
                const isSortedAsc = sortItem?.sort === 'asc';
                const isSortedDesc = sortItem?.sort === 'desc'
                return (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
                </div>
            )}, type: 'string', 
            flex: 0.5, minWidth: 120, align: 'left', headerAlign: 'left', filterOperators,
            renderCell: (params) => <div>{formatUtcToLocal(params.value, 'MM-DD-YYYY')}</div>,
        },
        { field: 'sentBy', headerName: 'Sent By', 
            hideSortIcons: true,
            renderHeader: (params) => {
                const sortItem = sortModel.find(
                    (item: any) => item.field === 'sentBy'
                  );
            
                const isSortedAsc = sortItem?.sort === 'asc';
                const isSortedDesc = sortItem?.sort === 'desc'
                return (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
                </div>
            )}, type: 'string', 
            flex: 0.5, minWidth: 120, align: 'left', headerAlign: 'left', filterOperators,
            renderCell: (params) => (
                <div>{params.value === 'miscstaff' ? 'MISC team' : (params.value || '-')}</div>
            )
        },
        {
            field: 'responseReceivedOn', headerName: 'Response Received', 
            hideSortIcons: true,
            renderHeader: (params: any) => {
                const sortItem = sortModel.find(
                    (item: any) => item.field === 'responseReceivedOn'
                  );
            
                const isSortedAsc = sortItem?.sort === 'asc';
                const isSortedDesc = sortItem?.sort === 'desc'
                return (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
                </div>
            )}, type: 'string', 
            flex: 0.8, minWidth: 200, align: 'left' as const, 
            headerAlign: 'left' as const, filterOperators,
            renderCell: (params: any) => <div>{params.value != null && params.value !== '' ? formatUtcToLocal(params.value, 'MM-DD-YYYY') : 'N/A'}</div>
        },
        
    ];

    useEffect(() => {
        fetchPartnersData();
        fetchStates();
        fetchErrorTagCodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        getAllReportTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, pageSize, filterModel, sortModel, reportTagListTab]);
     
    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const reportTagIdParam = searchParams.get('reportTagId');
        const openAttachments = searchParams.get('openAttachments') === 'true';
        const openMessages = searchParams.get('openMessages') === 'true';

        if (reportTagIdParam && openAttachments) {
            const id = parseInt(reportTagIdParam, 10);
            if (!isNaN(id) && id > 0) {
                window.history.replaceState({}, '', window.location.pathname);
                viewDocumentsAttached(id);
            }
        } else if (reportTagIdParam && openMessages) {
            const id = parseInt(reportTagIdParam, 10);
            if (!isNaN(id) && id > 0) {
                window.history.replaceState({}, '', window.location.pathname);
                setMiscMessageCenterReportTagId(id);
                setMiscMessageCenterOpen(true);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
 
    const fetchPartnersData = () => {
        setLoading(true);
        apiClient('/Partners/getPartners', {
            method: "GET",
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            })
        }).then((responseJson) => {
            if (!responseJson) return;
            setPartners(responseJson.data);
        }).catch(error => {
            console.error("Error fetching partners:", error)
        }).finally(() => {
            setLoading(false);
        });
    };
    const fetchStates = () => {
        setLoading(true);
        apiClient('/States/getStates', {
            method: "GET",
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            })
        }).then((responseJson) => {
            if (!responseJson) return;
            setStates(responseJson.data);
        }).catch(error => {
            console.error("Error fetching states:", error)
        }).finally(() => {
            setLoading(false);
        });
    };
    const getAllReportTags = () => {
        setLoading(true);
        
        // Convert filterModel to the format expected by the API (alias MISC team -> miscstaff for sentBy)
        const filterModelJson =
            filterModel && filterModel.items && filterModel.items.length > 0
                ? applyReportTagSentByAliasToFilterModel(filterModel)
                : null;
        
        // Get sort field and direction from sortModel
        const sortField = sortModel && sortModel.length > 0 ? sortModel[0].field || '' : '';
        const sortDirection = sortModel && sortModel.length > 0 ? sortModel[0].sort || '' : '';
        
        apiClient('/ReportTags/getAllReportTags', {
            method: 'POST',
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            }),
            body: JSON.stringify({
                pageSize: pageSize,
                pageNumber: currentPage,
                field: sortField,
                sort: sortDirection,
                filterModel: filterModelJson,
                archivedOnly: reportTagListTab === ReportTagTabNames.Archive,
            })
        }).then((responseJson) => {
            if (!responseJson || !responseJson.data) return;
            setReportTags(responseJson.data.reportTags);
            setTotalCount(responseJson.data.totalCount);
        }).catch(error => {
            console.error("Error fetching report tags:", error);
            setReportTags([]);
            setTotalCount(0);
        }).finally(() => {
            setLoading(false);
        });
    };

    const reportTagPartnerOptions = partners.filter((p) => p.partnersId > 0);
    const reportTagStateOptions = states.filter((s) => s.stateId > 0);

    const handleReportTagFilterSearch = () => {
        const items: GridFilterModel['items'] = [];
        let nextId = 1;
        if (filterBarPartner && filterBarPartner.partnersId > 0 && filterBarPartner.customerName) {
            items.push({ id: nextId++, columnField: 'agency', operatorValue: 'equals', value: filterBarPartner.customerName });
        }
        if (filterBarState && filterBarState.stateId > 0 && filterBarState.postalAbbv) {
            items.push({ id: nextId++, columnField: 'state', operatorValue: 'equals', value: filterBarState.postalAbbv });
        }
        const sent = filterBarSentBy.trim();
        if (sent) {
            items.push({ id: nextId++, columnField: 'sentBy', operatorValue: 'contains', value: sent });
        }
        setFilterModel({ items, linkOperator: GridLinkOperator.And });
        setCurrentPage(1);
    };

    const handleReportTagFilterClear = () => {
        setFilterBarPartner(null);
        setFilterBarState(null);
        setFilterBarSentBy('');
        setFilterModel({ items: [] });
        setCurrentPage(1);
    };

    const handleReportTagFilterRefresh = () => {
        window.location.reload();
    };

    const fetchErrorTagCodes = () => {
        setLoading(true);
        apiClient('/ReportTags/getAllErrorTags', {
            method: "GET",
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            })
        }).then((responseJson) => {
            if (!responseJson) return;
            setErrorTagCodes(responseJson.data);
        }).catch(error => {
            console.error("Error fetching error tag codes:", error)
        }).finally(() => {
            setLoading(false);
        });
    };

    const viewDocumentsAttached = (reportTagId: number) => {
        setDocumentListOpen(true);
        setLoading(true);
        apiClient(`/ReportTags/getReportTagDocumentsById/${reportTagId}`, {
            method: 'GET',
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            })
        }).then((response) => {
            if (!response) return;
            const { data } = response;
            if (data && data.documents && data.documents.length > 0) {
                const documents: ReportTagDocument[] = data.documents.map((doc: any) => ({
                    reportTagId: doc.reportTagId || 0,
                    reportTagDocumentId: doc.reportTagDocumentId || 0,
                    documentUrl: doc.documentUrl,
                    documentName: doc.documentName || 'Document',
                    createdByUserId: doc.createdByUserId || 0,
                    createdByUsername: doc.createdByUsername || 'Unknown',
                    createdByUserRole: doc.createdByUserRole || '',
                    dateCreated: new Date(doc.dateCreated) || '',
                }));
                setDocumentList(documents);
            } else {
                setDocumentListOpen(false);
                Swal.fire({
                    title: 'No Documents',
                    text: 'No documents found for this tag',
                    icon: 'info',
                    confirmButtonText: 'OK',
                });
            }
        }).catch((error) => {
            console.error("Error fetching documents:", error);
            setDocumentListOpen(false);
            Swal.fire({
                title: 'Error',
                text: 'Failed to fetch documents',
                icon: 'error',
                confirmButtonText: 'OK',
            });
        }).finally(() => {
            setLoading(false);
        });
    };
    const deleteReportTagDocument = (document: ReportTagDocument) => {
        setLoading(true);
        Swal.fire({
            title: '<div style="color: white;">Are you sure?</div>',
            html: `<div style="color: white;">This will delete the document</div>`,
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'No, Cancel',
            confirmButtonColor: GreenBtnColor,
            cancelButtonColor: OrangeBtnColor,
            reverseButtons: true,
            background: PrimaryBlueColor,
        }).then((result) => {
            if (result.isConfirmed) {
                apiClient(`/ReportTags/deleteReportTagDocument?reportTagId=${document.reportTagId}&reportTagDocumentId=${document.reportTagDocumentId}`, {
                    method: 'DELETE',
                    headers: new Headers({
                        'Authorization': `Bearer ${token}`,
                    }),
                }).then((response) => {
                    if (!response) return;
                    Swal.fire({
                        title: 'Document deleted successfully',
                        text: 'The document has been deleted successfully',
                        icon: 'success',
                        confirmButtonText: 'OK',
                    });
                    // remove document from document list
                    setDocumentList(documentList.filter(doc => doc.reportTagDocumentId !== document.reportTagDocumentId));
                    // refresh the document list
                    handleCloseDocumentViewer();
                    viewDocumentsAttached(document.reportTagId);

                }).catch((error) => {
                    console.error("Error deleting document:", error);
                    Swal.fire({
                        title: 'Error',
                        text: 'Failed to delete document',
                        icon: 'error',
                        confirmButtonText: 'OK',
                    });
                }).finally(() => {
                    setLoading(false);
                });
            }
        }).finally(() => {
            setLoading(false);
        });
    };
    
    const handleCloseDocumentList = () => {
        setDocumentListOpen(false);
        // Only clear if viewer is not open (user closed list without viewing)
        if (!documentViewerOpen) {
            setDocumentList([]);
            setCurrentDocumentIndex(-1);
        }
        if (window.location.search) {
            window.history.replaceState({}, '', window.location.pathname);
        }
    };
    const handleSelectDocument = (document: ReportTagDocument, index: number) => {
        setCurrentDocumentIndex(index);
        setDocumentListOpen(false); // Close list but keep documents in state
        handleViewDocument(document.documentUrl, document.documentName);
    };
    
    const handleBackToList = () => {
        // Close viewer and show list again
        setDocumentViewerOpen(false);
        if (documentViewerUrl) {
            URL.revokeObjectURL(documentViewerUrl);
        }
        setDocumentViewerUrl(null);
        setDocumentViewerFileName(null);
        setViewingDocument(false);
        // Show the list dialog if there are multiple documents
        if (documentList.length > 1) {
            setDocumentListOpen(true);
        }
    };
    
    const handlePreviousDocument = () => {
        if (currentDocumentIndex > 0 && documentList.length > 0) {
            const newIndex = currentDocumentIndex - 1;
            setCurrentDocumentIndex(newIndex);
            handleViewDocument(documentList[newIndex].documentUrl, documentList[newIndex].documentName);
        }
    };
    
    const handleNextDocument = () => {
        if (currentDocumentIndex < documentList.length - 1 && documentList.length > 0) {
            const newIndex = currentDocumentIndex + 1;
            setCurrentDocumentIndex(newIndex);
            handleViewDocument(documentList[newIndex].documentUrl, documentList[newIndex].documentName);
        }
    };
    const handleViewDocument = (blobAddress: string, fileName?: string) => {
        if (!blobAddress) {
            Swal.fire({
                title: 'Error',
                text: 'No document URL available',
                icon: 'error',
                confirmButtonText: 'OK',
            });
            return;
        }

        // Revoke previous blob URL if it exists to prevent memory leaks
        if (documentViewerUrl) {
            URL.revokeObjectURL(documentViewerUrl);
        }

        // Open dialog immediately to show loading
        setViewingDocument(true);
        setDocumentViewerOpen(true);
        setDocumentViewerFileName(fileName || "document.pdf");

        try {
            const blobUrl = blobAddress;
            // Set the blob URL to display in the viewer
            setDocumentViewerUrl(blobUrl);
            setViewingDocument(false);
        } catch (error) {
            console.error("Error downloading document:", error);
            setViewingDocument(false);
            setDocumentViewerOpen(false);
            // If download fails, show error and fall back to opening in new tab
            Swal.fire({
                title: "Error",
                text: "Failed to download file. Opening in new tab instead.",
                icon: "warning",
                confirmButtonText: "OK",
            }).then(() => {
                window.open(blobAddress, "_blank");
            });
        }
    };
    const handleCloseDocumentViewer = () => {
        if (documentViewerUrl) {
            URL.revokeObjectURL(documentViewerUrl);
        }
        setDocumentViewerOpen(false);
        setDocumentViewerUrl(null);
        setDocumentViewerFileName(null);
        setViewingDocument(false);
        // Clear document list and index when fully closing
        setDocumentList([]);
        setCurrentDocumentIndex(-1);
    };

    const addNewTag = () => {
        setSelectedRows([]);
        // Only clear extraction state when there is nothing running or pending review —
        // if extraction is in progress or complete the dialog should resume that session.
        if (!extractionInProgress && !extractionComplete) {
            clearExtraction();
        }
        setAddReportTagOpen(true);
    };

    const deleteSelectedTags = () => {
        if(selectedRows.length !== 1) {
            Swal.fire({
                title: 'Select one tag',
                text: 'Delete works on a single tag. Select one row, then Delete.',
                icon: 'warning',
                confirmButtonColor: '#02A4E3',
                confirmButtonText: 'OK',
            });
        }else{
            Swal.fire({
                title: '<div style="color: white;">Are you sure?</div>',
                html: `<div style="color: white;">You won't be able to revert this!</div>`,
                showCancelButton: true,
                confirmButtonText: 'Yes, Delete',
                cancelButtonText: 'No, Continue',
                confirmButtonColor: GreenBtnColor,
                cancelButtonColor: OrangeBtnColor,
                reverseButtons: true,
                background: PrimaryBlueColor,
            }).then((result) => {
                if(result.isConfirmed) {
                    setLoading(true);
                    const tagIdList = selectedRows.map(row => row.reportTagId).join(',');
                    apiClient(`/ReportTags/delete?tagIdList=${tagIdList}`, {
                        method: 'DELETE',
                        headers: new Headers({
                            'Authorization': `Bearer ${token}`,
                        }),
                    }).then((response) => {
                        setLoading(false);
                        
                        // delete logic here
                        if(response.status){
                            // reload the report data
                            getAllReportTags();
                            setSelectedRows([]);
                            Swal.fire({
                                title: 'Tags deleted successfully',
                                text: "The tags have been deleted successfully",
                                icon: 'success',
                                showCancelButton: false,
                                showDenyButton: false,
                                confirmButtonColor: '#02A4E3',
                                confirmButtonText: 'OK',
                            });
                        }else{
                            Swal.fire({
                                title: 'Error',
                                text: "Failed to delete tags",
                                icon: 'error',
                                showCancelButton: true,
                                confirmButtonColor: '#02A4E3',
                                cancelButtonColor: '#d33',
                                confirmButtonText: 'OK',
                            });
                        }
                    }).catch((error) => {
                        setLoading(false);
                        Swal.fire({
                            title: 'Error',
                            text: "Failed to delete tags",
                            icon: 'error',
                            showCancelButton: true,
                            confirmButtonColor: '#02A4E3',
                            cancelButtonColor: '#d33',
                            confirmButtonText: 'OK',
                        });
                    });
                }
            });
        }
    };

    const downloadDocument = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, doc: ReportTagDocument) => {
        e.stopPropagation();
        try {
            setLoading(true);
            const response = await fetch(doc.documentUrl, {
                method: 'GET',
                mode: 'cors',
                credentials: 'omit',
                cache: 'no-cache',
            });
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = doc.documentName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error: any) {
            console.error('Error downloading document:', error);
            Swal.fire({
                title: 'Error',
                text: 'Failed to download document',
                icon: 'error',
                confirmButtonText: 'OK',
            });
        } finally {
            setLoading(false);
        }
    };

    //#region UI Elements
    const getDetailPanelHeight = useCallback(() => 200, []);
    const getDetailPanelContent = useCallback<NonNullable<DataGridProProps['getDetailPanelContent']>>(({ row }) => (
        <DetailPanelContent row={row} />
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ), [errorTagCodes]);
    function DetailPanelContent({ row }: { row: ReportTagModel }){
        const errorTagRows = row.errorTagCodeIds?.map(id => {
            const errorTag = errorTagCodes.find(code => code.errorTagCodeId === id);
            return errorTag ? errorTag : null;
        }).filter((item): item is { errorTagCodeId: number; errorTagName: string } => item !== null) ?? [];
        return (
            <Stack direction="column" spacing={1} sx={{ height: 1 }} className="hide-border-12 ps-5">
                <DataGridPro 
                    columns={[
                        {
                            field: 'errorTagName', headerName: 'Error Type', 
                            type: 'string',
                            flex: 1,
                            headerAlign: 'left',
                            align: 'left',
                            hideSortIcons: true,
                            renderHeader: (params) => (
                                <div>
                                    <span className="table-card-title">{params.colDef.headerName}</span>
                                    <RiExpandUpDownLine />
                                </div>
                            ),
                            renderCell: (params) => (
                                <Typography variant="body2" sx={{ color: 'text.primary' }}>{`${params.row.errorTagCodeId}: ${params.row.errorTagName}`}</Typography>
                            ),
                        }
                    ]}
                    rows={errorTagRows}
                    getRowId={(r) => r?.errorTagCodeId ?? 0}
                    sx={{ flex: 1,
                        '& .MuiDataGrid-columnHeader': {
                            backgroundColor: '#f8fafc',
                            // borderTop: '2px solid #e2e8f0',
                            borderBottom: '2px solid #e2e8f0',
                            minHeight: '52px !important',
                            maxHeight: '52px !important',
                        },
                     }}
                    hideFooter
                />
            </Stack>
        );
    };
    function CustomToolbar() {
        return (
            <GridToolbarContainer>
                <GridToolbarFilterButton className="me-3 sky-blue" />
                <GridToolbarColumnsButton className="me-3 sky-blue" />
                <GridToolbarExport className="me-3 sky-blue" />
                <GridToolbarDensitySelector className="me-3 sky-blue" />
            </GridToolbarContainer>
        );
    };
    function EditToolbar() {
        const [toolbarMenuAnchor, setToolbarMenuAnchor] = useState<null | HTMLElement>(null);
        const openToolbarMenu = Boolean(toolbarMenuAnchor);

        const handleToolbarMenuClose = () => setToolbarMenuAnchor(null);
        const handleToolbarAction = (action: 'filter' | 'columns' | 'density' | 'exportCsv' | 'exportPrint') => {
            handleToolbarMenuClose();
            const api = reportTagGridApiRef.current;
            if (!api) return;
            if (action === 'filter') api.showFilterPanel();
            else if (action === 'columns') (api as any).showPreferences('columns');
            else if (action === 'density') (api as any).showPreferences('density');
            else if (action === 'exportCsv') api.exportDataAsCsv();
            else if (action === 'exportPrint') api.exportDataAsPrint();
        };

        return (
            <div className="p-2 d-flex justify-content-between align-items-center datagrid-butons flex-wrap" style={{ gap: 8 }}>
                        <Tabs
                            value={reportTagListTab}
                            onChange={(_, newValue) => handleReportTagTabChange(newValue)}
                            aria-label="Report tag list tabs"
                            sx={{
                                display: { xs: 'none', md: 'flex' },
                                minHeight: 48,
                                marginRight: 2,
                                '& .MuiButtonBase-root': { minHeight: '0px' },
                                '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.9375rem' },
                                '& .Mui-selected': { color: '#2099d2' },
                                '& .MuiTabs-indicator': { backgroundColor: '#2099d2', height: 3, borderRadius: '3px 3px 0 0' },
                            }}
                        >
                            <Tab label="Open" value={ReportTagTabNames.Open} icon={<FaPaperPlane fontSize="small" />} iconPosition="start" />
                            <Tab label="Closed" value={ReportTagTabNames.Archive} />
                        </Tabs>
                        <FormControl
                            size="small"
                            sx={{
                                display: { xs: 'block', md: 'none' },
                                minWidth: 160,
                                marginRight: 2,
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
                                '& .MuiSelect-select': { fontWeight: 600, color: '#2099d2', py: 0.9 },
                            }}
                        >
                            <Select
                                value={reportTagListTab}
                                onChange={(e) => handleReportTagTabChange(e.target.value as ReportTagTabNames)}
                                displayEmpty
                                sx={{ textTransform: 'none', fontSize: '0.9375rem' }}
                            >
                                <MenuItem value={ReportTagTabNames.Open}>Open</MenuItem>
                                <MenuItem value={ReportTagTabNames.Archive}>Closed</MenuItem>
                            </Select>
                        </FormControl>
                <Box className="ms-auto" sx={{ display: 'flex', alignItems: 'center' }}>
                    {isTabletOrDown ? (
                        <>
                            <IconButton
                                aria-label="Table options"
                                aria-controls={openToolbarMenu ? 'table-options-menu' : undefined}
                                aria-haspopup="true"
                                aria-expanded={openToolbarMenu ? 'true' : undefined}
                                onClick={(e) => setToolbarMenuAnchor(e.currentTarget)}
                                sx={{ color: '#2099d2' }}
                            >
                                <MoreVertOutlinedIcon />
                            </IconButton>
                            <Menu
                                id="table-options-menu"
                                anchorEl={toolbarMenuAnchor}
                                open={openToolbarMenu}
                                onClose={handleToolbarMenuClose}
                                MenuListProps={{ 'aria-labelledby': 'table-options-button' }}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            >
                                <MenuItem onClick={() => handleToolbarAction('filter')}>Filter</MenuItem>
                                <MenuItem onClick={() => handleToolbarAction('columns')}>Columns</MenuItem>
                                <MenuItem onClick={() => handleToolbarAction('density')}>Density</MenuItem>
                                <MenuItem onClick={() => handleToolbarAction('exportCsv')}>Download as CSV</MenuItem>
                                <MenuItem onClick={() => handleToolbarAction('exportPrint')}>Print</MenuItem>
                            </Menu>
                        </>
                    ) : (
                        <CustomToolbar />
                    )}
                </Box>
            </div>
        );
    };
    //#endregion

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div style={{ position: "fixed", top: "0", left: "0", zIndex: 99999, width: "100%", pointerEvents: "none" }}>
                {loading && <LinearProgress color="info" sx={{ backgroundColor: "#132D3D" }} />}
            </div>
            <Box sx={{ width: "100%" }}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: 1.5,
                        px: 2,
                        py: 1.5,
                        borderBottom: "1px solid #e2e8f0",
                        backgroundColor: "#fafbfc",
                        minHeight: 56,
                        marginLeft: { xs: '-24px', md: '0px' },
                        marginRight: { xs: '-24px', md: '0px' },
                    }}
                >
                    <h4 className="mb-0 py-2" style={{ fontWeight: 600, color: "#0f172a" }}>I Want To... Report a Tag</h4>
                        <Box sx={{ width: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Button
                                onClick={addNewTag}
                                variant="contained"
                                size="medium"
                                startIcon={<FaPlus fontSize="small" />}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    backgroundColor: LightBlueBtnColor,
                                    '&:hover': { backgroundColor: '#176990' },
                                    px: 2,
                                }}
                            >
                                New Tag
                            </Button>
                            {!addReportTagOpen && (extractionInProgress || extractionComplete) && (
                                <Chip
                                    label={extractionInProgress ? 'Extraction in progress…' : 'Extraction complete — click to review'}
                                    color={extractionInProgress ? 'info' : 'success'}
                                    size="small"
                                    onClick={extractionComplete ? () => { setExtractionComplete(false); setAddReportTagOpen(true); } : undefined}
                                    sx={{ cursor: extractionComplete ? 'pointer' : 'default', fontWeight: 500 }}
                                />
                            )}
                            <Button
                                variant="contained"
                                size="medium"
                                onClick={deleteSelectedTags}
                                disabled={selectedRows.length === 0}
                                startIcon={<FaTrash fontSize="small" />}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    backgroundColor: '#dc2626',
                                    '&:hover': { backgroundColor: '#b91c1c' },
                                    px: 2,
                                }}
                            >
                                Delete
                            </Button>
                        </Box>
                </Box>
            </Box>
            {/* Queue filter row: Search replaces filterModel (including DataGrid toolbar filters). Clear resets bar + filterModel. Refresh = full page reload (same as Filing Queue). */}
            <Box sx={{ width: '100%', py: 2, px: 2 }}>
                <Grid container spacing={2} className="filter-grid" display="flex">
                        <Grid item xl={2} lg={4} sm={6} xs={12}>
                            <FormControl fullWidth>
                                <Autocomplete
                                    sx={{
                                        marginTop: '-4px !important',
                                        '& .MuiAutocomplete-input': { padding: '0px 4px 0px 5px !important' },
                                    }}
                                    options={reportTagPartnerOptions}
                                    getOptionLabel={(option) => option.customerName}
                                    isOptionEqualToValue={(a, b) => a.partnersId === b.partnersId}
                                    value={filterBarPartner}
                                    onChange={(_, v) => setFilterBarPartner(v)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Client"
                                            size="small"
                                            sx={{
                                                '& .MuiInputBase-root': { marginTop: '5px !important' },
                                                '& .MuiOutlinedInput-root': { padding: '4.5px 9px' },
                                                '& .MuiInputLabel-root': { transform: 'translate(14px, 12px) scale(1)' },
                                                '& .MuiInputLabel-shrink': { transform: 'translate(14px, -9px) scale(0.75)' },
                                            }}
                                        />
                                    )}
                                />
                            </FormControl>
                        </Grid>
                    <Grid item xl={2} lg={4} sm={6} xs={12}>
                        <FormControl fullWidth>
                            <Autocomplete
                                sx={{
                                    marginTop: '-4px !important',
                                    '& .MuiAutocomplete-input': { padding: '0px 4px 0px 5px !important' },
                                }}
                                options={reportTagStateOptions}
                                getOptionLabel={(option) => option.stateName}
                                isOptionEqualToValue={(a, b) => a.stateId === b.stateId}
                                value={filterBarState}
                                onChange={(_, v) => setFilterBarState(v)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="State"
                                        size="small"
                                        sx={{
                                            '& .MuiInputBase-root': { marginTop: '5px !important' },
                                            '& .MuiOutlinedInput-root': { padding: '4.5px 9px' },
                                            '& .MuiInputLabel-root': { transform: 'translate(14px, 12px) scale(1)' },
                                            '& .MuiInputLabel-shrink': { transform: 'translate(14px, -9px) scale(0.75)' },
                                        }}
                                    />
                                )}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xl={2} lg={4} sm={6} xs={12}>
                        <TextField
                            name="reportTagFilterSentBy"
                            size="small"
                            label="Sent By"
                            value={filterBarSentBy}
                            onChange={(e) => setFilterBarSentBy(e.target.value)}
                            fullWidth
                            variant="outlined"
                            sx={{
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#c4c4c4' },
                                '& .MuiInputBase-root': { marginTop: '1px !important' },
                                '& .MuiInputBase-input': { padding: '6px 14px !important' },
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button
                                variant="contained"
                                size="medium"
                                onClick={handleReportTagFilterSearch}
                                startIcon={<Search />}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    backgroundColor: '#2099d2',
                                    '&:hover': { backgroundColor: '#176990' },
                                }}
                            >
                                Search
                            </Button>
                            <Button
                                variant="outlined"
                                size="medium"
                                onClick={handleReportTagFilterClear}
                                startIcon={<Clear />}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderColor: '#e2e8f0',
                                    color: '#475569',
                                    '&:hover': { borderColor: '#2099d2', color: '#2099d2' },
                                }}
                            >
                                Clear
                            </Button>
                            <Button
                                variant="outlined"
                                size="medium"
                                onClick={handleReportTagFilterRefresh}
                                startIcon={<Refresh />}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderColor: '#e2e8f0',
                                    color: '#475569',
                                    '&:hover': {
                                        borderColor: '#2099d2',
                                        backgroundColor: '#2099d214',
                                        color: '#2099d2',
                                    },
                                }}
                            >
                                Refresh
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
            <Box sx={{ width: "100%" }}>
                    
                <div
                    style={{
                        height: "calc(100vh - 300px)",
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                        borderRadius: "12px",
                        overflow: "hidden",
                        boxShadow: "0 4px 24px rgba(15, 23, 42, 0.08), 0 1px 3px rgba(15, 23, 42, 0.06)",
                        border: "1px solid #e2e8f0",
                        backgroundColor: "#ffffff",
                        position: "relative",
                    }}
                >
                    {loading && (
                        <Box
                            position="absolute"
                            top={0}
                            left={0}
                            right={0}
                            bottom={0}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                                backgroundColor: "rgba(250, 251, 252, 0.85)",
                                zIndex: 10,
                                border: "1px solid #e2e8f0",
                                borderRadius: 1,
                            }}
                        >
                            <Box sx={{ width: "100%", maxWidth: 320 }}>
                                <LinearProgress color="info" sx={{ backgroundColor: "#e2e8f0", "& .MuiLinearProgress-bar": { backgroundColor: "#132D3D" } }} />
                                <Box sx={{ mt: 1.5, textAlign: "center" }}>
                                    <span style={{ fontSize: "14px", fontWeight: 600, color: "#475569" }}>Loading...</span>
                                </Box>
                            </Box>
                        </Box>
                    )}
                    <div style={{ flex: '1 1 0%', overflow: 'auto' }}>
                        <DataGridPro
                            apiRef={reportTagGridApiRef}
                            rows={reportTags}
                            getRowId={(r) => r?.reportTagId ?? 0}
                            columns={columns}
                            getDetailPanelHeight={getDetailPanelHeight}
                            getDetailPanelContent={getDetailPanelContent}

                            page={currentPage - 1}
                            pageSize={pageSize}
                            pagination
                            rowCount={totalCount}
                            paginationMode="server"
                            onPageChange={(newPage) => setCurrentPage(newPage + 1)}
                            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                            rowsPerPageOptions={getRowsPerPageOptions(totalCount, pageSize)}
                            filterMode="server"
                            filterModel={filterModel}
                            onFilterModelChange={(newFilterModel) => {
                                setFilterModel(newFilterModel);
                                setCurrentPage(1); // Reset to first page when filter changes
                            }}
                            sortingMode="server"
                            sortingOrder={['asc', 'desc']}
                            sortModel={sortModel}
                            onSortModelChange={(newSortModel) => {
                                setSortModel(newSortModel ?? []);
                                setCurrentPage(1); // Reset to first page when sort changes
                            }}
                            checkboxSelection={false}
                            selectionModel={selectedRows.map(row => row.reportTagId).filter((id): id is number => id !== undefined)}
                            onSelectionModelChange={(newSelectionModel) => {
                                const ids = (newSelectionModel as number[]);
                                const lastId = ids.length > 0 ? ids[ids.length - 1] : undefined;
                                const selectedObjects = lastId == null
                                    ? []
                                    : reportTags.filter((tag) => tag.reportTagId === lastId);
                                setSelectedRows(selectedObjects);
                            }}
                            
                            components={{
                                Toolbar: EditToolbar,
                            }}
                            sx={{
                                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                border: 'none',
                                fontSize: '14px',
                                color: '#475569',
                                backgroundColor: '#ffffff',
                                "& .MuiDataGrid-columnHeaderTitle": {
                                    whiteSpace: 'normal',
                                    lineHeight: 1.35,
                                    fontWeight: 600,
                                    fontSize: '16px',
                                    letterSpacing: '0.05em',
                                    color: '#162E3C',
                                },
                                '& .MuiDataGrid-columnHeaders': {
                                    backgroundColor: '#f8fafc',
                                    borderTop: '2px solid #e2e8f0',
                                    borderBottom: '2px solid #e2e8f0',
                                    minHeight: '52px !important',
                                    maxHeight: '52px !important',
                                },
                                '& .MuiDataGrid-columnHeader': {
                                    '&:focus, &:focus-within': { outline: 'none' },
                                },
                                '& .MuiDataGrid-row': {
                                    transition: 'background-color 0.15s ease',
                                    '&:nth-of-type(even)': {
                                        backgroundColor: '#fafbfc',
                                    },
                                    '&:hover': {
                                        backgroundColor: '#f1f5f9 !important',
                                    },
                                   '&.Mui-selected': {
                                        backgroundColor: '#ebf5fa !important',
                                        '&:hover': {
                                            backgroundColor: '#dbeef7 !important',
                                        },
                                    },
                                },
                                '& .MuiDataGrid-cell': {
                                    borderBottom: '1px solid #f1f5f9',
                                    fontSize: '14px',
                                    color: '#334155',
                                    '&:focus': { outline: 'none' },
                                },
                                '& .MuiDataGrid-cell[data-field="filerNotes"]': {
                                    alignItems: 'center',
                                    overflow: 'hidden',
                                },
                                ".MuiDataGrid-virtualScroller" : {
                                    border: 'none',
                                },
                                "& .MuiDataGrid-main": {
                                    border: 'none',
                                },
                                "& .MuiDataGrid-footerContainer": {
                                    borderTop: '1px solid #e2e8f0',
                                    backgroundColor: '#f8fafc',
                                    minHeight: 56,
                                },
                                "& .MuiTablePagination-root": {
                                    color: '#64748b',
                                    fontSize: '13px',
                                },
                                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                                    marginBottom: 0,
                                },
                                "& .MuiTablePagination-select": {
                                    fontSize: '13px',
                                    borderRadius: '6px',
                                    border: '1px solid #e2e8f0',
                                },
                                "& .MuiIconButton-root": {
                                    color: '#64748b',
                                    "&:hover": {
                                        backgroundColor: '#f1f5f9',
                                        color: '#475569',
                                    },
                                },
                                "& .MuiCheckbox-root": {
                                    color: '#94a3b8',
                                    "&.Mui-checked": {
                                        color: '#2099d2',
                                    },
                                },
                            }}
                        />
                    </div>
                </div>

            </Box>
            <Box sx={{ width: '80%' }}>
                <AddReportTag Open={addReportTagOpen} handleClose={handleCloseAddReportTag}
                    partners={partners}
                    states={states}
                    errorTagCodes={errorTagCodes}
                />
            </Box>
            
            <Dialog
                open={filerNotesFullDialog.open}
                onClose={() => setFilerNotesFullDialog((prev) => ({ ...prev, open: false }))}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <DialogTitle sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#162E3C', pb: 1 }}>
                    {filerNotesFullDialog.title}
                </DialogTitle>
                <DialogContent dividers sx={{ pt: 2, pb: 2, backgroundColor: '#fafbfc' }}>
                    <Box sx={{ backgroundColor: '#fff', borderRadius: 1, p: 2, border: '1px solid #e2e8f0' }}>
                        <FilerNotesCellContent text={filerNotesFullDialog.text} />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        variant="contained"
                        onClick={() => setFilerNotesFullDialog((prev) => ({ ...prev, open: false }))}
                        sx={{ textTransform: 'none', backgroundColor: PrimaryBlueColor }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Document List Dialog (for multiple documents) */}
            <ViewReportTagAttachments open={documentListOpen} 
                handleClose={handleCloseDocumentList} 
                documentList={documentList} 
                loading={loading} 
                handleSelectDocument={handleSelectDocument} 
                downloadDocument={downloadDocument}
            />
            
            {/* Document Viewer Dialog */}
            <DocumentViewer open={documentViewerOpen}
                documents={documentList}
                onClose={handleBackToList}
            />

            <MiscReportTagMessageDialog
                open={miscMessageCenterOpen}
                reportTagId={miscMessageCenterReportTagId}
                token={token}
                queue={reportTagListTab === ReportTagTabNames.Archive ? 'closed' : 'open'}
                onClose={() => {
                    setMiscMessageCenterOpen(false);
                    setMiscMessageCenterReportTagId(0);
                }}
                onRefresh={getAllReportTags}
            />

            {/* Mark as Unresolved Dialog */}
            <Dialog open={markAsUnresolvedOpen} onClose={closeMarkAsUnresolvedDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
                <DialogTitle className="card-title" sx={{ textAlign: 'center', pb: 0, fontWeight: 600, mb: 2 }}>
                    Mark tag as Unresolved
                </DialogTitle>
                <DialogContent sx={{ pt: 2, pb: 1 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 56, height: 56, borderRadius: '50%', backgroundColor: '#fef3c7', color: '#d97706' }}>
                            <SettingsBackupRestoreOutlinedIcon sx={{ fontSize: 32 }} />
                        </Box>
                        <Box>
                            <Typography sx={{ color: '#334155', fontSize: '15px', lineHeight: 1.5, mb: 1 }}>
                                Send this tag back to the client?
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#64748b', fontSize: '13px' }}>
                                The tag will be marked as unresolved and returned to the client for further action.
                            </Typography>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <Button onClick={closeMarkAsUnresolvedDialog} variant="outlined" sx={{ minWidth: 100, textTransform: 'none', fontWeight: 600, borderColor: '#e2e8f0', color: '#475569', '&:hover': { borderColor: '#cbd5e1', backgroundColor: '#f8fafc' } }}>
                        Cancel
                    </Button>
                    <Button onClick={sendTagBackToClient} variant="contained" sx={{ minWidth: 160, textTransform: 'none', fontWeight: 600, backgroundColor: PrimaryBlueColor, '&:hover': { backgroundColor: '#237291' } }}>
                        Yes, send back to client
                    </Button>
                </DialogActions>
            </Dialog>
        </LocalizationProvider>
    );
};
interface ViewReportTagAttachmentsProps {
    open: boolean;
    handleClose: () => void;
    documentList: ReportTagDocument[];
    loading: boolean;
    handleSelectDocument: (doc: ReportTagDocument, index: number) => void;
    downloadDocument: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, doc: ReportTagDocument) => void;
};
const ViewReportTagAttachments: React.FC<ViewReportTagAttachmentsProps> = ({ open, handleClose, documentList, loading, handleSelectDocument, downloadDocument }) => {
    const closeAttachmentsDialog = () => {
        handleClose();
    };
    
    const columns: GridColDef[] = [
        { field: 'documentName', headerName: 'Document Name', 
            flex: 3, minWidth: 250, align: 'left', headerAlign: 'left', type: 'string', 
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ), 
        },
        { field: 'dateCreated', headerName: 'Date Uploaded', 
            flex: 0.8, minWidth: 130, align: 'center', headerAlign: 'center', type: 'date', 
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ), 
            renderCell: (params) => (
                <div>
                    {formatUtcToLocal(params.value, 'MM-DD-YYYY')}
                </div>
            )
        },
        {
            field: 'createdByUsername', headerName: 'Uploaded By', 
            flex: 0.8, minWidth: 130, align: 'center', headerAlign: 'center', type: 'string', 
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ),
        },
        { field: 'action', headerName: 'Download', 
            hideSortIcons: true,
            type: 'string', 
            flex: 0.6, minWidth: 120, align: 'left', headerAlign: 'center', 
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'left', justifyContent: 'left' }}>
                    <Tooltip title="View Document" arrow={true}>
                        <IconButton sx={{ color: LightBlueBtnColor }}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (params.row.documentUrl && params.row.documentName) {
                                    handleSelectDocument(params.row, 0);
                                }
                            }} 
                        ><FaRegEye /></IconButton>
                    </Tooltip>
                    <Tooltip title="Download Documents" arrow={true}>
                        <IconButton sx={{ color: LightBlueBtnColor }}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (params.row.reportTagId) {
                                    downloadDocument(e, params.row);
                                }
                            }}
                        ><FaDownload /></IconButton>
                    </Tooltip>
                </Box>
            )
        },
    ];
    
    return (
        <Dialog open={open} onClose={closeAttachmentsDialog} maxWidth="lg" fullWidth>
            <DialogTitle className="card-title">Select Document to view</DialogTitle>
            <DialogContent>
                <Box overflow={"scroll"}>
                    <Box sx={{ p: 3 }}>
                        <div style={{ height: "59vh", display: "flex", flexDirection: "column", width: "100%" }}>
                            <div style={{ flex: 1, overflow: "auto" }}>
                                <DataGridPro rows={documentList}
                                    getRowId={(r) => r?.reportTagDocumentId ?? 0}
                                    columns={columns}
                                    sx={{
                                        border: '0px',
                                    }}
                                />
                            </div>
                        </div>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <button className="btn-2 btn d-flex btn-primary search-btn" type="button" onClick={closeAttachmentsDialog}>Close</button>
            </DialogActions>
        </Dialog>
    );
};
export default ReportTag;